import { useCallback, useRef, useState } from 'react';

/**
 * Guards a submit handler against duplicate submissions.
 *
 * The latch is a ref, so it is set synchronously at click time instead of one
 * render later like a state update would be. That closes the window in which a
 * fast double click (or a click landing while Formik still awaits its async
 * validation) fires the same request twice.
 *
 * `isSubmitting` is returned separately for rendering the spinner and disabling
 * the button; `run` never relies on it.
 */
export const useSubmitOnce = (): {
    isSubmitting: boolean;
    run: (submit: () => Promise<void>) => Promise<void>;
} => {
    const isRunningRef = useRef(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const run = useCallback(async (submit: () => Promise<void>): Promise<void> => {
        if (isRunningRef.current) {
            return;
        }
        isRunningRef.current = true;
        setIsSubmitting(true);
        try {
            await submit();
        } finally {
            isRunningRef.current = false;
            setIsSubmitting(false);
        }
    }, []);

    return { isSubmitting, run };
};
